/**
 * App Section
 * Displays basic application settings
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { DcsApp } from '../../types/general-settings-types';
import { Input } from '@/core/components/ui/input';
import { Checkbox } from '@/core/components/ui/checkbox';
import { Label } from '@/core/components/ui/label';
import { LogoSelector } from '../atoms/LogoSelector';

interface AppSectionProps {
  app: DcsApp;
  onFieldChange: (field: string, value: any) => void;
  hasChanges?: boolean;
  isOpen: boolean;
  onToggle: () => void;
  isCreateMode?: boolean;
}

export const AppSection = ({ app, onFieldChange, hasChanges, isOpen, onToggle, isCreateMode }: AppSectionProps) => {
  const { t } = useTranslation('assignEv');

  return (
    <div className="rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      {/* Section Header */}
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between bg-gray-100 hover:bg-gray-200 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isOpen ? (
            <ChevronDown className="w-5 h-5 text-gray-700" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-700" />
          )}
          <h3 className="font-bold text-gray-900 text-base">{t('general.appSection.title')}</h3>
          {hasChanges && (
            <span className="ml-2 px-3 py-1 text-xs font-bold text-white bg-orange-500 rounded-full shadow-md">
              {t('badges.modified')}
            </span>
          )}
        </div>
      </button>

      {/* Section Content */}
      {isOpen && (
        <div className="px-6 py-4 bg-white space-y-8">
          {/* ===== IDENTIFICATION ===== */}
          <div>
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-4 pb-2 border-b border-gray-200">
              {t('general.appSection.subsections.identification')}
            </h4>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('general.appSection.fields.appId')}
                </Label>
                <Input
                  value={app.appId || ''}
                  disabled
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('general.appSection.fields.operatorId')}
                </Label>
                <Input
                  value={app.operatorId}
                  disabled
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('general.appSection.fields.name')}
                  {isCreateMode && <span className="ml-1 text-red-500">*</span>}
                </Label>
                <Input
                  value={app.name || ''}
                  onChange={(e) => onFieldChange('name', e.target.value)}
                  className={isCreateMode && !app.name ? 'border-yellow-400 bg-yellow-50 focus-visible:ring-yellow-400' : 'bg-white border-gray-300'}
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('general.appSection.fields.slug')}
                  {isCreateMode && <span className="ml-1 text-red-500">*</span>}
                </Label>
                <Input
                  value={app.slug || ''}
                  onChange={(e) => onFieldChange('slug', e.target.value)}
                  className={isCreateMode && !app.slug ? 'border-yellow-400 bg-yellow-50 focus-visible:ring-yellow-400' : 'bg-white border-gray-300'}
                />
              </div>
            </div>
          </div>

          {/* ===== BRANDING ===== */}
          <div>
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-4 pb-2 border-b border-gray-200">
              {t('general.appSection.subsections.branding')}
            </h4>
            <LogoSelector
              value={app.logoUrl}
              onChange={(url) => onFieldChange('logoUrl', url)}
            />
          </div>

          {/* ===== SETTINGS ===== */}
          <div>
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-4 pb-2 border-b border-gray-200">
              {t('general.appSection.subsections.settings')}
            </h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-medium text-gray-600 mb-2 block">
                  {t('general.appSection.fields.smsKey')}
                </Label>
                <Input
                  value={app.smsKey || ''}
                  onChange={(e) => onFieldChange('smsKey', e.target.value)}
                  className="bg-white border-gray-300"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs font-medium text-gray-600 mb-2 block opacity-0">
                  Spacer
                </Label>
                <div className="flex items-center gap-3 h-10">
                  <Checkbox
                    id="showPriceOnSession"
                    checked={app.showPriceOnSession || false}
                    onCheckedChange={(checked) =>
                      onFieldChange('showPriceOnSession', checked)
                    }
                  />
                  <Label htmlFor="showPriceOnSession" className="text-sm cursor-pointer">
                    {t('general.appSection.fields.showPriceOnSession')}
                  </Label>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
